import type { UsageDailyRow, UsageFilterParams } from "./usageApi";

export function buildUsageQueryParams(filters: UsageFilterParams = {}) {
  return {
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    userEmail: filters.userEmail || undefined,
    limit: filters.limit || 200,
    includeRootUsers: filters.includeRootUsers ? "1" : undefined,
    mspId: filters.mspId != null && filters.mspId !== undefined ? String(filters.mspId) : undefined,
  };
}

const formatDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const startOfLocalDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const addCalendarDays = (d: Date, days: number): Date => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
};

export type UsageQuickDatePresetId =
  | "custom"
  | "last_day"
  | "last_7_days"
  | "last_30_days"
  | "last_week"
  | "this_month"
  | "last_month"
  | "this_year"
  | "last_year";

export function getUsageDateRangeForPreset(
  id: UsageQuickDatePresetId,
  now: Date = new Date()
): { startDate: string; endDate: string } | null {
  if (id === "custom") return null;
  const today = startOfLocalDay(now);
  switch (id) {
    case "last_day": {
      const y = addCalendarDays(today, -1);
      const s = formatDateInputValue(y);
      return { startDate: s, endDate: s };
    }
    case "last_7_days": {
      const start = addCalendarDays(today, -6);
      return { startDate: formatDateInputValue(start), endDate: formatDateInputValue(today) };
    }
    case "last_30_days": {
      const start = addCalendarDays(today, -29);
      return { startDate: formatDateInputValue(start), endDate: formatDateInputValue(today) };
    }
    case "last_week": {
      const dow = today.getDay();
      const mondayOffset = dow === 0 ? -6 : 1 - dow;
      const thisMonday = addCalendarDays(today, mondayOffset);
      const lastSunday = addCalendarDays(thisMonday, -1);
      const lastMonday = addCalendarDays(lastSunday, -6);
      return { startDate: formatDateInputValue(lastMonday), endDate: formatDateInputValue(lastSunday) };
    }
    case "this_month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: formatDateInputValue(start), endDate: formatDateInputValue(today) };
    }
    case "last_month": {
      const firstThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastMonthEnd = addCalendarDays(firstThisMonth, -1);
      const lastMonthStart = new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1);
      return { startDate: formatDateInputValue(lastMonthStart), endDate: formatDateInputValue(lastMonthEnd) };
    }
    case "this_year": {
      const start = new Date(today.getFullYear(), 0, 1);
      return { startDate: formatDateInputValue(start), endDate: formatDateInputValue(today) };
    }
    case "last_year": {
      const y = today.getFullYear() - 1;
      const start = new Date(y, 0, 1);
      const end = new Date(y, 11, 31);
      return { startDate: formatDateInputValue(start), endDate: formatDateInputValue(end) };
    }
    default:
      return null;
  }
}

export const USAGE_QUICK_RANGE_OPTIONS: { id: UsageQuickDatePresetId; label: string }[] = [
  { id: "custom", label: "Custom" },
  { id: "last_day", label: "Last day" },
  { id: "last_7_days", label: "Last 7 days" },
  { id: "last_30_days", label: "Last 30 days" },
  { id: "last_week", label: "Last week" },
  { id: "this_month", label: "This month" },
  { id: "last_month", label: "Last month" },
  { id: "this_year", label: "This year" },
  { id: "last_year", label: "Last year" },
];

export const USAGE_DEFAULT_QUICK_PRESET: UsageQuickDatePresetId = "last_7_days";

/**
 * Parse a `YYYY-MM-DD` string as a local calendar date (aligned with date inputs and quick presets).
 * Returns null if the string is not a valid calendar day.
 */
function parseYmdLocal(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) {
    return null;
  }
  return startOfLocalDay(dt);
}

/**
 * For Usage "by day" panels: include every calendar day from filter start through end (inclusive).
 * Days with no audit rows show `actions: 0`. Order matches the UI (newest first by default).
 * If either date is missing or invalid, returns `rows` unchanged (sparse API data).
 */
export interface ConsolidatedAuditFilterParams {
  startDate?: string | null;
  endDate?: string | null;
  customerId?: number | null;
  ticketId?: string | null;
  limit?: number;
  includeRootUsers?: boolean;
  mspId?: number | null;
}

export function buildConsolidatedAuditQueryParams(filters: ConsolidatedAuditFilterParams = {}) {
  return {
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    customerId: filters.customerId != null ? String(filters.customerId) : undefined,
    ticketId: filters.ticketId?.trim() || undefined,
    limit: filters.limit ?? 1000,
    includeRootUsers: filters.includeRootUsers ? "1" : undefined,
    mspId: filters.mspId != null && filters.mspId !== undefined ? String(filters.mspId) : undefined,
  };
}

export function expandDailyUsageForRange(
  startDate: string,
  endDate: string,
  rows: UsageDailyRow[],
  order: "desc" | "asc" = "desc"
): UsageDailyRow[] {
  const start = parseYmdLocal(startDate);
  const end = parseYmdLocal(endDate);
  if (!start || !end) {
    return rows;
  }
  let from = start;
  let to = end;
  if (from > to) {
    const swap = from;
    from = to;
    to = swap;
  }
  const byDate = new Map<string, number>();
  for (const r of rows) {
    const key = typeof r.date === "string" ? r.date.trim() : "";
    if (key) {
      byDate.set(key, Number(r.actions) || 0);
    }
  }
  const daysAsc: string[] = [];
  for (let cur = new Date(from); cur <= to; cur = addCalendarDays(cur, 1)) {
    daysAsc.push(formatDateInputValue(cur));
  }
  const ordered = order === "desc" ? [...daysAsc].reverse() : daysAsc;
  return ordered.map((date) => ({
    date,
    actions: byDate.get(date) ?? 0,
  }));
}
